/**
 * AI Section Content Component
 * 
 * Displays specific AI-generated sections within existing accordions.
 * Fetches and displays parsed sections from stored summaries.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Collapse,
  IconButton,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import AssistantOutlinedIcon from '@mui/icons-material/AssistantOutlined';
import { getStoredSummary } from '../../services/aiService';
import ReactMarkdown from 'react-markdown';

const THEME_COLOR = '#673ab7';

/**
 * Filter content based on mode
 * @param {string} content - The markdown content to filter
 * @param {string} mode - Filter mode: 'all', 'implications', 'nearby_schools', or 'enrollment_comparison'
 * @returns {string} - Filtered content
 */
const filterContent = (content, mode) => {
  if (mode === 'all' || !content) return content;
  
  const lines = content.split('\n');
  
  if (mode === 'implications') {
    // Keep only bullets with strategic implication language
    const implicationKeywords = [
      'implies', 'planning', 'require', 'likely', 'will depend',
      'pressure', 'if capacity', 'rather than', 'maintaining',
      'may face', 'capturing', 'attractiveness', 'driven by',
      'fewer naturally', 'increasing market share', 'drawing transfers',
      'tailwind', 'headwind', 'demographic'
    ];
    
    const filteredLines = lines
      .filter(line => {
        const trimmed = line.trim();
        // Only keep bullet points that contain implication keywords
        if (!trimmed.startsWith('-') && !trimmed.startsWith('•')) return false;
        return implicationKeywords.some(kw => trimmed.toLowerCase().includes(kw));
      })
      .map(line => {
        // Strip bullet markers and convert to paragraph text
        return line.trim().replace(/^[-•]\s*/, '');
      });
    
    // Join with double newlines to create separate paragraphs
    return filteredLines.join('\n\n');
  }
  
  if (mode === 'nearby_schools') {
    // Extract nearby schools section (list header + school bullets)
    let inNearbySection = false;
    let foundSchools = false;
    
    const filteredLines = lines.filter(line => {
      const trimmed = line.trim();
      
      // Start of nearby schools section
      if (trimmed.toLowerCase().includes('nearby schools')) {
        inNearbySection = true;
        return true;
      }
      
      // School list items (indented bullets with school names)
      if (inNearbySection && (trimmed.startsWith('○') || trimmed.startsWith('◦') || trimmed.startsWith('*'))) {
        foundSchools = true;
        return true;
      }
      
      // Regular bullets within nearby section that look like school data
      if (inNearbySection && trimmed.startsWith('-') && trimmed.includes(':') && trimmed.includes('**')) {
        // Check if it looks like a school entry (has enrollment numbers)
        if (/\d+/.test(trimmed) && (trimmed.includes('enrollment') || trimmed.includes('change'))) {
          return false; // Skip the selected school's enrollment line
        }
      }
      
      // End of nearby section when we hit a non-indented bullet that's an implication
      if (inNearbySection && foundSchools && trimmed.startsWith('-') && !trimmed.startsWith('○') && !trimmed.startsWith('◦')) {
        inNearbySection = false;
        return false;
      }
      
      return false;
    });
    
    return filteredLines.join('\n');
  }

  if (mode === 'enrollment_comparison') {
    // Extract comparison takeaways and implications from Enrollment Context
    // These provide strategic insights about school comparisons (not population trends)
    const comparisonKeywords = [
      'takeaway', 'comparison', 'signaling', 'experienced', 'outpaces',
      'shifting', 'preferences', 'mid-to-upper', 'mid-to-lower',
      'strongest', 'weakest', 'largest', 'smallest', 'above-average',
      'below-average', 'momentum', 'sits near', 'compared with'
    ];
    
    // Exclude lines that are population-level implications (shown in Enrollment Trends section)
    const isPopulationImplication = (line) => {
      const lower = line.toLowerCase();
      return (
        lower.includes('population rising') ||
        lower.includes('population growing') ||
        lower.includes('population declining') ||
        lower.includes('incremental demand') ||
        lower.includes('these enrollment trends suggest') ||
        lower.includes('capturing that demand')
      );
    };
    
    // Also exclude lines that are just data lists (school names with enrollment numbers)
    const isDataLine = (line) => {
      const trimmed = line.trim();
      // Lines that list schools with enrollment numbers like "**School Name** **1,234** (+17%)"
      if (trimmed.includes('**') && /\d{3,}/.test(trimmed) && /%/.test(trimmed)) {
        // Check if it's multiple schools listed (comma or semicolon separated)
        if ((trimmed.match(/\*\*/g) || []).length > 4) return true;
      }
      // Lines starting with "Nearby schools" that list schools
      if (trimmed.toLowerCase().startsWith('- nearby schools')) return true;
      // Lines with just school enrollment info
      if (trimmed.match(/^\*\*[^*]+\*\*.*enrollment/i)) return true;
      return false;
    };
    
    const filteredLines = lines
      .filter(line => {
        const trimmed = line.trim();
        // Skip empty lines
        if (!trimmed) return false;
        // Skip data-heavy lines
        if (isDataLine(trimmed)) return false;
        // Skip population-level implications (already in Enrollment Trends)
        if (isPopulationImplication(trimmed)) return false;
        // Only keep bullet points
        if (!trimmed.startsWith('-') && !trimmed.startsWith('•')) return false;
        // Keep lines with comparison/insight keywords
        return comparisonKeywords.some(kw => trimmed.toLowerCase().includes(kw));
      })
      .map(line => {
        // Strip bullet markers and convert to paragraph text
        return line.trim().replace(/^[-•]\s*/, '');
      });
    
    // Join with double newlines to create separate paragraphs
    return filteredLines.join('\n\n');
  }
  
  return content;
};

/**
 * AISectionContent - Displays AI-generated section content
 * 
 * @param {Object} props
 * @param {Object} props.selectedSchool - The selected school object (must have uuid)
 * @param {number} props.driveTime - Drive time in minutes (5, 10, or 15)
 * @param {string} props.summaryType - Type of summary: 'geographic', 'competitive', or 'projections'
 * @param {string|Array<string>} props.sectionTitle - Title(s) of section(s) to display
 * @param {string} props.filterMode - Filter mode: 'all' (default), 'implications', 'nearby_schools', or 'enrollment_comparison'
 * @param {string} props.fallbackMessage - Message to show when no content available
 * @param {boolean} props.defaultExpanded - Whether content is expanded by default
 * @param {boolean} props.collapsible - Whether content can be collapsed
 */
const AISectionContent = ({ 
  selectedSchool, 
  driveTime = 10, 
  summaryType = 'geographic',
  travelMode = 'drive',
  sectionTitle,
  filterMode = 'all',
  fallbackMessage = 'AI analysis not available',
  defaultExpanded = true,
  collapsible = true,
  isRegenerating = false // Show loading overlay when regenerating
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [loading, setLoading] = useState(false);
  const [sectionContent, setSectionContent] = useState(null);
  const [error, setError] = useState(null);

  // Memoize section titles to prevent re-renders
  const sectionTitles = useMemo(() => {
    return Array.isArray(sectionTitle) ? sectionTitle : [sectionTitle];
  }, [sectionTitle]);

  // Load section content when school or parameters change
  useEffect(() => {
    const loadSection = async () => {
      if (!selectedSchool?.uuid || !sectionTitle) {
        setSectionContent(null);
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        const result = await getStoredSummary(selectedSchool.uuid, driveTime, summaryType, travelMode);
        
        if (result.found && result.sections) {
          // Try to find matching section(s)
          const sections = result.sections.sections || [];
          const matchingContent = [];
          
          for (const title of sectionTitles) {
            const titleLower = title.toLowerCase();
            const matchingSection = sections.find(s => 
              s.title?.toLowerCase().includes(titleLower) ||
              titleLower.includes(s.title?.toLowerCase())
            );
            
            if (matchingSection) {
              matchingContent.push(matchingSection);
            }
          }
          
          if (matchingContent.length > 0) {
            setSectionContent(matchingContent);
          } else {
            setSectionContent(null);
          }
        } else {
          setSectionContent(null);
        }
      } catch (err) {
        console.error('Error loading AI section:', err);
        // Silently fail for access errors - component will just not render
        if (err.message && err.message.includes('Access denied')) {
          setSectionContent(null);
          setError(null);
        } else {
          setError(err.message);
          setSectionContent(null);
        }
      } finally {
        setLoading(false);
      }
    };

    loadSection();
  }, [selectedSchool?.uuid, driveTime, summaryType, travelMode, sectionTitle, sectionTitles]);

  // Don't render if no school selected or no section title
  if (!selectedSchool?.uuid || !sectionTitle) {
    return null;
  }

  // Don't render while loading - completely invisible
  // EXCEPTION: If isRegenerating is true, keep the component visible
  if (loading && !isRegenerating) {
    return null;
  }

  // Don't render if there's an error
  if (error && !isRegenerating) {
    return null;
  }

  // No content available
  // EXCEPTION: If isRegenerating is true, keep the component visible to show regeneration overlay
  if ((!sectionContent || sectionContent.length === 0) && !isRegenerating) {
    return null;
  }

  // Check if filtered content would be empty
  const hasFilteredContent = sectionContent && sectionContent.some(section => {
    const filtered = filterContent(section.content, filterMode);
    return filtered && filtered.trim().length > 0;
  });

  if (!hasFilteredContent && !isRegenerating) {
    return null; // Don't show if all filtered content is empty
  }

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 1.5,
        mb: 1,
        backgroundColor: '#f8f5ff',
        border: '1px solid',
        borderColor: `${THEME_COLOR}40`,
        borderRadius: 1.5,
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 0.75,
          backgroundColor: `${THEME_COLOR}15`,
          borderBottom: expanded ? '1px solid' : 'none',
          borderColor: `${THEME_COLOR}30`,
          cursor: collapsible ? 'pointer' : 'default'
        }}
        onClick={() => collapsible && setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <AssistantOutlinedIcon sx={{ color: THEME_COLOR, fontSize: 18 }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: THEME_COLOR }}>
            AI Insights
          </Typography>
          <Chip 
            label="AI Generated" 
            size="small" 
            sx={{ 
              height: 18,
              fontSize: '0.65rem',
              backgroundColor: `${THEME_COLOR}20`,
              color: THEME_COLOR,
              '& .MuiChip-label': { px: 0.75 }
            }}
          />
        </Box>
        {collapsible && (
          <IconButton size="small" sx={{ color: THEME_COLOR, p: 0.25 }}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        )}
      </Box>

      {/* Content */}
      <Collapse in={expanded}>
        <Box sx={{ px: 1.5, py: 1, position: 'relative' }}>
          {/* Show regeneration indicator when regenerating and no content */}
          {isRegenerating && (!sectionContent || sectionContent.length === 0) ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              gap: 1, 
              py: 2,
              minHeight: 80
            }}>
              <CircularProgress size={24} sx={{ color: THEME_COLOR }} />
              <Typography variant="body2" sx={{ color: THEME_COLOR, fontWeight: 500 }}>
                Updating AI insights with new data...
              </Typography>
            </Box>
          ) : (
            <>
              {/* Regeneration Overlay when content exists */}
              {isRegenerating && (
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(248, 245, 255, 0.95)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  zIndex: 1,
                  borderRadius: 1
                }}>
                  <CircularProgress size={24} sx={{ color: THEME_COLOR }} />
                  <Typography variant="body2" sx={{ color: THEME_COLOR, fontWeight: 500 }}>
                    Updating AI insights with new data...
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ opacity: isRegenerating ? 0.4 : 1, transition: 'opacity 0.3s ease' }}>
                {sectionContent && sectionContent.map((section, index) => (
                  <Box key={index} sx={{ mb: index < sectionContent.length - 1 ? 1 : 0 }}>
                    {sectionContent.length > 1 && (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600, 
                          color: THEME_COLOR, 
                          mb: 0.5 
                        }}
                      >
                        {section.title}
                      </Typography>
                    )}
                    <Box sx={{ 
                      '& p': { mb: 0.75, lineHeight: 1.5, fontSize: '0.875rem' },
                      '& p:first-of-type': { mt: 0 },
                      '& p:last-child': { mb: 0 },
                      '& strong': { fontWeight: 600 },
                      '& ul': { pl: 2, mb: 0.75, fontSize: '0.875rem', mt: 0 },
                      '& li': { mb: 0.25, lineHeight: 1.4 }
                    }}>
                      <ReactMarkdown>{filterContent(section.content, filterMode) || ''}</ReactMarkdown>
                    </Box>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default AISectionContent;

